import { useCallback, useEffect, useState } from 'react'

import { filteredText, runFiles } from './experimentOverviewStats'

// The experiment page's Presenter (ExperimentPresenterTab): code the instructor shows the class
// and runs - their own, or a student's, added from the Overview ("Show in Presenter"). The
// Presenter is projected, so a student's code comes with an anonymous label only ('Student 7 ·
// NameError', the Overview's numbering), never their name, and through the Overview's word
// filter (filteredText) - the label, the note and the code alike. Kept in the page's state for
// the experiment and in this browser's storage, per experiment; nothing goes to the server.
// Two windows of one experiment in this browser (the Presenter on the projector, the Overview
// on the laptop) share the list; each has its own editor, kept for its own reloads.
// The experiment's answer key (the server's, fetched by the Presenter) is pinned above the
// list: it loads as a listed item does, but is never stored with the list, removed or cleared.

// The most code the list keeps: the newest.
export const PRESENTER_MAX_ITEMS = 20

export const presenterStorageKey = (experimentId) => `vizpi.experimentPresenter.${experimentId}`

// items: [{ id, label, detail, code, addedAt }], the newest first; selectedId: the item last
// loaded into the editor (null for none, or once it is removed); code: the editor's text.
export const EMPTY_PRESENTER = { items: [], selectedId: null, code: '' }

// The labels of what the instructor typed in the editor, as it goes to the list before other
// code replaces it (keepEditorCode): their own code, and a listed item's label with this after
// it, for that item's code changed in the editor.
export const OWN_CODE_LABEL = 'Your code'
export const EDITED_SUFFIX = ' (edited)'

// The pinned answer key's item: its id (never one of newId's) and what the list shows.
export const ANSWER_KEY_ID = 'answer-key'
export const ANSWER_KEY_LABEL = 'Answer key'

// The server's answer key ({ exerciseId, code }) as the pinned item - its code with the line
// breaks the editor has - or null for no key. Not through the word filter: it is the
// exercise's own reference solution, not a student's.
export const answerKeyItem = (key) => {
  if (typeof key?.code !== 'string' || !key.code.trim()) return null
  return {
    id: ANSWER_KEY_ID,
    label: ANSWER_KEY_LABEL,
    detail: 'The reference solution',
    code: key.code.replace(/\r\n?/g, '\n'),
    addedAt: 0,
  }
}

// What the Presenter can load: the pinned answer key, when there is one, then the list.
export const loadableItems = (presenter) => (presenter.answerKey ? [presenter.answerKey, ...presenter.items] : presenter.items)

// Whether `selectedId` is still something loadable: a listed item's id, or the answer key's -
// kept while the key is being fetched (after a reload), so the pinned row is selected again.
const stillSelectable = (items, selectedId) => selectedId === ANSWER_KEY_ID || items.some((item) => item.id === selectedId)

// The code a run recorded (TERMINAL_RUN's payload.code) as the Presenter can take it: one file
// (runFiles), not empty. null for anything else - a web exercise's HTML, CSS and JS together,
// an empty or missing record.
export const presentableCode = (code) => {
  if (typeof code !== 'string' || !code.trim()) return null
  const files = runFiles(code)
  return files.length === 1 && !files[0].name ? files[0].code : null
}

// What an error is, short, for a Presenter label: the type an error key starts with
// ('NameError' of "NameError: name 'x' is not defined"), else the key itself, cut short.
export const errorLabel = (key) => {
  const text = String(key ?? '')
  const type = /^([A-Za-z_][\w.]*):\s/.exec(text)?.[1]
  if (type) return type
  return text.length > 40 ? `${text.slice(0, 39)}…` : text
}

// What a student wrote, as the Presenter shows and keeps it: through the word filter, with the
// line breaks the Overview's code view shows.
const shownCode = (code) => String(filteredText(String(code ?? '')) ?? '').replace(/\r\n?/g, '\n')

const text = (value) => (typeof value === 'string' ? value : '')

// A stored list as the Presenter can use it: anything malformed is left out.
export const normalizePresenter = (stored) => {
  const items = (Array.isArray(stored?.items) ? stored.items : [])
    .filter((item) => item && typeof item.id === 'string' && typeof item.code === 'string' && typeof item.label === 'string')
    .slice(0, PRESENTER_MAX_ITEMS)
    .map((item) => ({ id: item.id, label: item.label, detail: text(item.detail), code: item.code, addedAt: Number(item.addedAt) || 0 }))
  return {
    items,
    selectedId: stillSelectable(items, stored?.selectedId) ? stored.selectedId : null,
    code: text(stored?.code),
  }
}

// What this browser kept for an experiment, or an empty Presenter.
export const readPresenter = (experimentId) => {
  if (experimentId == null) return EMPTY_PRESENTER
  try {
    return normalizePresenter(JSON.parse(window.localStorage.getItem(presenterStorageKey(experimentId)) || 'null'))
  } catch (error) {
    // Storage blocked or unreadable: the Presenter starts empty.
    return EMPTY_PRESENTER
  }
}

// The answer key never goes into the record every window of the experiment shares: a window
// opened later (the projector) would start with the reference solution in its editor, and an
// Overview-only window, which never fetches the key, would add it as 'Your code'. The window
// that loaded it gets it back on a reload from its own editor record (saveEditor).
const savePresenter = (experimentId, { items, selectedId, code }) => {
  const shared = selectedId === ANSWER_KEY_ID ? { items, selectedId: null, code: '' } : { items, selectedId, code }
  try {
    window.localStorage.setItem(presenterStorageKey(experimentId), JSON.stringify(shared))
  } catch (error) {
    // Storage full or blocked: the Presenter still holds while the page stays open.
  }
}

// This window's own editor - its text, the item last loaded into it and the code last loaded
// (keepEditorCode's `loaded`) - kept in this tab's sessionStorage, which a reload keeps and no
// other window shares: what was typed on the projector is not written over by the laptop's
// editor, stored in the experiment's list since.
export const presenterEditorKey = (experimentId) => `vizpi.experimentPresenter.editor.${experimentId}`

const readEditor = (experimentId) => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(presenterEditorKey(experimentId)) || 'null')
    if (typeof stored?.code !== 'string') return null
    return {
      selectedId: typeof stored.selectedId === 'string' ? stored.selectedId : null,
      code: stored.code,
      loaded: typeof stored.loaded === 'string' ? stored.loaded : undefined,
    }
  } catch (error) {
    // Storage blocked or unreadable: as in a window opened anew.
    return null
  }
}

const saveEditor = (experimentId, { selectedId, code, loaded }) => {
  try {
    window.sessionStorage.setItem(presenterEditorKey(experimentId), JSON.stringify({ selectedId, code, loaded }))
  } catch (error) {
    // Storage full or blocked: the editor still holds while the page stays open.
  }
}

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

// The list with `item` at its top (in place of the item with its id, if listed), cut to the
// newest PRESENTER_MAX_ITEMS - but never cutting the item `keepId`, about to be loaded.
const withItem = (items, item, keepId) => {
  const list = [item, ...items.filter((other) => other.id !== item.id)]
  const kept = list.slice(0, PRESENTER_MAX_ITEMS)
  const keep = kept.some((other) => other.id === keepId) ? null : list.find((other) => other.id === keepId)
  return keep ? [...kept.slice(0, PRESENTER_MAX_ITEMS - 1), keep] : kept
}

// An entry ({ label, detail, code }, unfiltered) as an item, through the word filter, at the
// top of the list: the same label and code again is the item already listed, moved there.
const listEntry = (items, entry, { now, id, keepId }) => {
  const label = shownCode(entry?.label).replace(/\s+/g, ' ').trim() || 'Code'
  const code = shownCode(entry?.code)
  const detail = shownCode(entry?.detail).replace(/\s+/g, ' ').trim()
  const same = items.find((item) => item.label === label && item.code === code)
  const item = { id: same?.id ?? id, label, detail, code, addedAt: now }
  return { item, items: withItem(items, item, keepId ?? item.id) }
}

/**
 * Adds code to the Presenter's list and loads it into the editor: `entry` is { label, detail,
 * code } - the label ('Student 7 · NameError'), a note under it (the error's whole key, what the
 * run came to) and the code, all as the Overview has them, unfiltered. The same label and code
 * again is the item already listed, moved to the top. The list keeps the newest
 * PRESENTER_MAX_ITEMS. `now` and `id` are injectable for tests.
 */
export const addToPresenter = (presenter, entry, { now = Date.now(), id = newId() } = {}) => {
  const { item, items } = listEntry(presenter.items, entry, { now, id })
  return { ...presenter, items, selectedId: item.id, code: item.code }
}

/**
 * The Presenter with the editor's text added to its list, before other code replaces it in the
 * editor, so nothing the instructor typed is lost: text that is not a listed item as it was
 * loaded goes to the top, as their own code ('Your code') or as the loaded item changed
 * ('Student 7 · NameError (edited)', with no note: what it comes to is not known). Nothing is
 * added for an empty editor, for code already listed, for the code about to replace it
 * (`next`), or for the code last loaded into the editor and not changed since (`loaded`; after
 * a reload, the editor's text if a listed item has it): an item removed from the list does not
 * come back. `keepId`, the item
 * about to be loaded, stays listed however full the list. `now` and `id` are injectable for
 * tests.
 */
export const keepEditorCode = (presenter, { next = '', loaded, keepId, now = Date.now(), id = newId() } = {}) => {
  const { code, items } = presenter
  const listed = loadableItems(presenter)
  if (!code.trim() || code === next || code === loaded || listed.some((item) => item.code === code)) return presenter
  const selected = listed.find((item) => item.id === presenter.selectedId)
  const label = !selected
    ? OWN_CODE_LABEL
    : selected.label.endsWith(EDITED_SUFFIX)
      ? selected.label
      : `${selected.label}${EDITED_SUFFIX}`
  return { ...presenter, items: listEntry(items, { label, code }, { now, id, keepId }).items }
}

const sameItems = (a, b) =>
  a.length === b.length &&
  a.every(
    (item, index) =>
      item.id === b[index].id &&
      item.label === b[index].label &&
      item.detail === b[index].detail &&
      item.code === b[index].code &&
      item.addedAt === b[index].addedAt
  )

/**
 * The Presenter with the list another window of the experiment stored (`items`, normalized):
 * that list, with this window's editor as it is - and its selection, while that item is still
 * listed. The same Presenter for the same list, as when only the other window's editor changed.
 */
export const adoptStoredList = (presenter, items) => {
  if (sameItems(presenter.items, items)) return presenter
  return {
    ...presenter,
    items,
    selectedId: stillSelectable(items, presenter.selectedId) ? presenter.selectedId : null,
  }
}

// What changes when `code` is loaded into the editor (add, select, blank, reset): the editor's
// text, the text last loaded (keepEditorCode's `loaded`) and how many loads there were.
const loading = (presenter, code) => ({ code, loaded: code, loads: (presenter.loads || 0) + 1 })

// An experiment's Presenter as this browser kept it, for the hook: the list, with this window's
// own editor as it was before a reload (readEditor). A window opened since starts with the
// editor this browser stored last, an editor holding a listed item's code counting as loaded
// with it (keepEditorCode's `loaded`), as in the window that stored it - so that item, removed
// or the list cleared, does not come back as 'Your code'.
const storedPresenter = (experimentId) => {
  const stored = readPresenter(experimentId)
  const own = experimentId == null ? null : readEditor(experimentId)
  if (own) {
    const selectedId = stillSelectable(stored.items, own.selectedId) ? own.selectedId : null
    return { experimentId, ...stored, selectedId, code: own.code, loaded: own.loaded }
  }
  const listed = stored.items.some((item) => item.code === stored.code)
  return { experimentId, ...stored, loaded: listed ? stored.code : undefined }
}

/**
 * The Presenter of one experiment: its list and the editor's text, from this browser's storage
 * and kept there on every change. When the page moves on to another experiment, that one's.
 * Returns { experimentId, items, answerKey, selectedId, code, loads, add(entry), select(id),
 * remove(id), clear(), setCode(code), reset(), blank(), setAnswerKey(key) } - `add` takes
 * addToPresenter's entry; `reset` puts the loaded item's code back in the editor; `blank`
 * empties the editor for the instructor's own code. `answerKey` is the pinned item
 * (answerKeyItem) the Presenter set with `setAnswerKey`, or null: `select` and `reset` take it
 * as a listed item; `remove` and `clear` leave it. `add`, `select` and `blank` first add what
 * the instructor typed in the editor to the list (keepEditorCode), so nothing typed is lost;
 * `reset` (Undo changes) discards it. `loads`
 * counts the code loaded into the editor in this window (add, select, blank, reset), for the
 * Presenter to tell a run's output from code loaded since.
 *
 * Another window of the experiment in this browser - the Presenter on the projector, the
 * Overview on the laptop - keeps the same list: each list it stores is taken here (the storage
 * event), so neither writes an older list back over the other's. The editor here stays as it
 * is, and a reload of this window brings back its own (readEditor), not the other's.
 */
export const useExperimentPresenter = (experimentId) => {
  // The stored { items, selectedId, code } and this window's own: the experiment's id, `loads`,
  // `loaded` (loading's, or storedPresenter's) and `adopted` - true for a list another window
  // stored, which this one does not write back.
  const [state, setState] = useState(() => storedPresenter(experimentId))

  useEffect(() => {
    if (state.experimentId !== experimentId) setState(storedPresenter(experimentId))
  }, [experimentId, state.experimentId])

  useEffect(() => {
    if (state.experimentId == null || state.adopted) return
    savePresenter(state.experimentId, state)
    saveEditor(state.experimentId, state)
  }, [state])

  // What another window stored for this experiment: its list, taken (adoptStoredList).
  useEffect(() => {
    if (experimentId == null) return undefined
    const key = presenterStorageKey(experimentId)
    const onStorage = (event) => {
      if (event.key !== key || typeof event.newValue !== 'string') return
      let stored
      try {
        stored = normalizePresenter(JSON.parse(event.newValue))
      } catch (error) {
        // Not a list this page stored: this window's stays.
        return
      }
      setState((previous) => {
        if (previous.experimentId !== experimentId) return previous
        const next = adoptStoredList(previous, stored.items)
        return next === previous ? previous : { ...next, adopted: true }
      })
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [experimentId])

  // A change to this experiment's Presenter: `change(presenter)` returns what changes.
  const update = useCallback(
    (change) =>
      setState((previous) => {
        const base = previous.experimentId === experimentId ? previous : storedPresenter(experimentId)
        return { ...base, ...change(base), adopted: false }
      }),
    [experimentId]
  )

  const add = useCallback(
    (entry) =>
      update((presenter) => {
        const kept = keepEditorCode(presenter, { next: shownCode(entry?.code), loaded: presenter.loaded })
        const added = addToPresenter(kept, entry)
        return { ...added, ...loading(presenter, added.code) }
      }),
    [update]
  )
  const select = useCallback(
    (id) =>
      update((presenter) => {
        const item = loadableItems(presenter).find((other) => other.id === id)
        if (!item) return {}
        const kept = keepEditorCode(presenter, { next: item.code, loaded: presenter.loaded, keepId: item.id })
        return { items: kept.items, selectedId: item.id, ...loading(presenter, item.code) }
      }),
    [update]
  )
  // The answer key is not removed, nor cleared with the list: loaded, it stays selected.
  const remove = useCallback(
    (id) =>
      update((presenter) =>
        id === ANSWER_KEY_ID
          ? {}
          : {
              items: presenter.items.filter((item) => item.id !== id),
              selectedId: presenter.selectedId === id ? null : presenter.selectedId,
            }
      ),
    [update]
  )
  const clear = useCallback(
    () => update((presenter) => ({ items: [], selectedId: presenter.selectedId === ANSWER_KEY_ID ? ANSWER_KEY_ID : null })),
    [update]
  )
  const setCode = useCallback((code) => update(() => ({ code: text(code) })), [update])
  const reset = useCallback(
    () =>
      update((presenter) => {
        const item = loadableItems(presenter).find((other) => other.id === presenter.selectedId)
        return item ? loading(presenter, item.code) : {}
      }),
    [update]
  )
  const blank = useCallback(
    () =>
      update((presenter) => {
        const kept = keepEditorCode(presenter, { loaded: presenter.loaded })
        return { items: kept.items, selectedId: null, ...loading(presenter, '') }
      }),
    [update]
  )

  // The answer key the Presenter fetched for this experiment (getAnswerKey's { exerciseId, code },
  // or null for none): pinned above the list, in this window only - never stored, so a reload
  // fetches it again. Not a change to the list, so nothing is written back for it.
  const setAnswerKey = useCallback(
    (key) =>
      setState((previous) => {
        const answerKey = answerKeyItem(key)
        if (previous.experimentId !== experimentId || (!answerKey && !previous.answerKey)) return previous
        return { ...previous, answerKey }
      }),
    [experimentId]
  )

  // Until the effect above has read another experiment's Presenter, that one's as stored.
  const current = state.experimentId === experimentId ? state : storedPresenter(experimentId)
  return {
    experimentId,
    items: current.items,
    answerKey: current.answerKey || null,
    selectedId: current.selectedId,
    code: current.code,
    loads: current.loads || 0,
    add,
    select,
    remove,
    clear,
    setCode,
    reset,
    blank,
    setAnswerKey,
  }
}
